// pages/reports/BalanceSheet.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
//import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    
    Download,
    Printer,
   
    DollarSign,
    TrendingUp,
    TrendingDown,
    PieChart,
    BarChart2,
    FileText,
    Grid,
    List,
   
    File,
    Calendar,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'feather-icons-react/build/IconComponents';
import { balanceSheetActions } from '../../core/action/balanceSheetActions';
//import LoadingSpinner from '../../core/components/LoadingSpinner';

const MySwal = withReactContent(Swal);

const BalanceSheet = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [balanceSheet, setBalanceSheet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [asOnDate, setAsOnDate] = useState(new Date());
    const [showZeroBalances, setShowZeroBalances] = useState(false);
    const [viewMode, setViewMode] = useState('detailed'); // 'summary', 'detailed', 'comparative'
    const [expandedSections, setExpandedSections] = useState({
        assets: true,
        liabilities: true,
        equity: true
    });
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load balance sheet data
    const loadBalanceSheet = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances
            };
            
            const result = await balanceSheetActions.getBalanceSheet(params);
            
            if (result.success) {
                setBalanceSheet(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load balance sheet',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading balance sheet:', error);
        } finally {
            setLoading(false);
        }
    }, [asOnDate, showZeroBalances]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadBalanceSheet();
    }, [loadBalanceSheet]);
    
    // Toggle section expansion
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };
    
    // Handle date change
    const handleDateChange = (date) => {
        setAsOnDate(date);
    };
    
    // Toggle zero balances
    const handleToggleZeroBalances = () => {
        setShowZeroBalances(prev => !prev);
    };
    
    // Handle refresh
    const handleRefresh = () => {
        loadBalanceSheet();
    };
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances,
                view_mode: viewMode
            };
            
            const result = await balanceSheetActions.exportToPDF(params);
            
            if (result) {
                // Create download link
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `balance_sheet_${formatDateForAPI(asOnDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances,
                view_mode: viewMode
            };
            
            const result = await balanceSheetActions.exportToExcel(params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `balance_sheet_${formatDateForAPI(asOnDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!balanceSheet) return null;
        
        const { summary } = balanceSheet;
        const isBalanced = summary.accounting_equation;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Assets</p>
                                    <h3 className="mb-0 text-primary">
                                        {balanceSheetActions.formatCurrency(summary.total_assets)}
                                    </h3>
                                    <small className="text-muted">
                                        {balanceSheetActions.formatPercentage(summary.total_assets, summary.total_assets + summary.total_liabilities)}
                                    </small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Liabilities</p>
                                    <h3 className="mb-0 text-danger">
                                        {balanceSheetActions.formatCurrency(summary.total_liabilities)}
                                    </h3>
                                    <small className="text-muted">
                                        {balanceSheetActions.formatPercentage(summary.total_liabilities, summary.total_assets + summary.total_liabilities)}
                                    </small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Equity</p>
                                    <h3 className="mb-0 text-success">
                                        {balanceSheetActions.formatCurrency(summary.total_equity)}
                                    </h3>
                                    <small className="text-muted">
                                        {balanceSheetActions.formatPercentage(summary.total_equity, summary.total_assets + summary.total_liabilities)}
                                    </small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Net Worth</p>
                                    <h3 className="mb-0 text-info">
                                        {balanceSheetActions.formatCurrency(summary.net_worth)}
                                    </h3>
                                    <small className="text-muted">
                                        {isBalanced ? (
                                            <span className="text-success">
                                                <CheckCircle size={12} /> Balanced
                                            </span>
                                        ) : (
                                            <span className="text-danger">
                                                <AlertCircle size={12} /> Unbalanced
                                            </span>
                                        )}
                                    </small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <PieChart size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render assets section
    const renderAssets = () => {
        if (!balanceSheet) return null;
        
        const { assets } = balanceSheet;
        
        return (
            <div className="card mb-4">
                <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <strong>ASSETS</strong>
                        <span className="ms-2 text-muted small">
                            Total: {balanceSheetActions.formatCurrency(assets.total)}
                        </span>
                    </h5>
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => toggleSection('assets')}
                    >
                        {expandedSections.assets ? <ChevronUp size={16} /> : <Grid size={16} />}
                    </button>
                </div>
                
                {expandedSections.assets && (
                    <div className="card-body">
                        {assets.categories.map((category, idx) => (
                            <div key={idx} className="mb-4">
                                <h6 className="text-primary mb-3">
                                    {category.category}
                                    <span className="ms-2 text-muted small">
                                        {balanceSheetActions.formatCurrency(category.total)}
                                    </span>
                                </h6>
                                
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Reference</th>
                                                <th className="text-end">Amount</th>
                                                <th className="text-end">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {category.items.map((item, itemIdx) => (
                                                <tr key={itemIdx}>
                                                    <td>
                                                        <strong>{item.name}</strong>
                                                        {item.is_default === 1 && (
                                                            <span className="badge bg-primary ms-2">Default</span>
                                                        )}
                                                    </td>
                                                    <td>{item.sub_name || '-'}</td>
                                                    <td>
                                                        <small className="text-muted">{item.reference || '-'}</small>
                                                    </td>
                                                    <td className="text-end">
                                                        {balanceSheetActions.formatCurrency(item.amount, item.currency)}
                                                    </td>
                                                    <td className="text-end">
                                                        {balanceSheetActions.formatPercentage(item.amount, assets.total)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-primary">
                                                <td colSpan="3" className="text-end">
                                                    <strong>Category Total</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatCurrency(category.total)}</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatPercentage(category.total, assets.total)}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        
                        <div className="row mt-3">
                            <div className="col-md-6 offset-md-6">
                                <table className="table table-sm">
                                    <tr className="table-primary">
                                        <td><strong>Total Assets</strong></td>
                                        <td className="text-end">
                                            <strong>{balanceSheetActions.formatCurrency(assets.total)}</strong>
                                        </td>
                                        <td className="text-end">
                                            <strong>100%</strong>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };
    
    // Render liabilities section
    const renderLiabilities = () => {
        if (!balanceSheet) return null;
        
        const { liabilities } = balanceSheet;
        
        return (
            <div className="card mb-4">
                <div className="card-header bg-danger bg-opacity-10 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <strong>LIABILITIES</strong>
                        <span className="ms-2 text-muted small">
                            Total: {balanceSheetActions.formatCurrency(liabilities.total)}
                        </span>
                    </h5>
                    <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => toggleSection('liabilities')}
                    >
                        {expandedSections.liabilities ? <ChevronUp size={16} /> : <Grid size={16} />}
                    </button>
                </div>
                
                {expandedSections.liabilities && (
                    <div className="card-body">
                        {liabilities.categories.map((category, idx) => (
                            <div key={idx} className="mb-4">
                                <h6 className="text-danger mb-3">
                                    {category.category}
                                    <span className="ms-2 text-muted small">
                                        {balanceSheetActions.formatCurrency(category.total)}
                                    </span>
                                </h6>
                                
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Reference</th>
                                                <th className="text-end">Amount</th>
                                                <th className="text-end">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {category.items.map((item, itemIdx) => (
                                                <tr key={itemIdx}>
                                                    <td>
                                                        <strong>{item.name}</strong>
                                                    </td>
                                                    <td>{item.sub_name || '-'}</td>
                                                    <td>
                                                        <small className="text-muted">{item.reference || '-'}</small>
                                                    </td>
                                                    <td className="text-end text-danger">
                                                        {balanceSheetActions.formatCurrency(item.amount, item.currency)}
                                                    </td>
                                                    <td className="text-end">
                                                        {balanceSheetActions.formatPercentage(item.amount, liabilities.total)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-danger">
                                                <td colSpan="3" className="text-end">
                                                    <strong>Category Total</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatCurrency(category.total)}</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatPercentage(category.total, liabilities.total)}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        
                        <div className="row mt-3">
                            <div className="col-md-6 offset-md-6">
                                <table className="table table-sm">
                                    <tr className="table-danger">
                                        <td><strong>Total Liabilities</strong></td>
                                        <td className="text-end">
                                            <strong>{balanceSheetActions.formatCurrency(liabilities.total)}</strong>
                                        </td>
                                        <td className="text-end">
                                            <strong>100%</strong>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };
    
    // Render equity section
    const renderEquity = () => {
        if (!balanceSheet) return null;
        
        const { equity } = balanceSheet;
        
        return (
            <div className="card mb-4">
                <div className="card-header bg-success bg-opacity-10 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <strong>EQUITY</strong>
                        <span className="ms-2 text-muted small">
                            Total: {balanceSheetActions.formatCurrency(equity.total)}
                        </span>
                    </h5>
                    <button 
                        className="btn btn-sm btn-outline-success"
                        onClick={() => toggleSection('equity')}
                    >
                        {expandedSections.equity ? <ChevronUp size={16} /> : <Grid size={16} />}
                    </button>
                </div>
                
                {expandedSections.equity && (
                    <div className="card-body">
                        {equity.categories.map((category, idx) => (
                            <div key={idx} className="mb-4">
                                <h6 className="text-success mb-3">
                                    {category.category}
                                    <span className="ms-2 text-muted small">
                                        {balanceSheetActions.formatCurrency(category.total)}
                                    </span>
                                </h6>
                                
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Reference</th>
                                                <th className="text-end">Amount</th>
                                                <th className="text-end">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {category.items.map((item, itemIdx) => (
                                                <tr key={itemIdx}>
                                                    <td>
                                                        <strong>{item.name}</strong>
                                                    </td>
                                                    <td>{item.sub_name || '-'}</td>
                                                    <td>
                                                        <small className="text-muted">{item.reference || '-'}</small>
                                                    </td>
                                                    <td className="text-end text-success">
                                                        {balanceSheetActions.formatCurrency(item.amount, item.currency)}
                                                    </td>
                                                    <td className="text-end">
                                                        {balanceSheetActions.formatPercentage(item.amount, equity.total)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-success">
                                                <td colSpan="3" className="text-end">
                                                    <strong>Category Total</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatCurrency(category.total)}</strong>
                                                </td>
                                                <td className="text-end">
                                                    <strong>{balanceSheetActions.formatPercentage(category.total, equity.total)}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        
                        <div className="row mt-3">
                            <div className="col-md-6 offset-md-6">
                                <table className="table table-sm">
                                    <tr className="table-success">
                                        <td><strong>Total Equity</strong></td>
                                        <td className="text-end">
                                            <strong>{balanceSheetActions.formatCurrency(equity.total)}</strong>
                                        </td>
                                        <td className="text-end">
                                            <strong>100%</strong>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };
    
    // Render accounting equation verification
    const renderEquationVerification = () => {
        if (!balanceSheet) return null;
        
        const { summary } = balanceSheet;
        const totalLeft = summary.total_assets;
        const totalRight = summary.total_liabilities + summary.total_equity;
        const difference = Math.abs(totalLeft - totalRight);
        const isBalanced = difference < 0.01; // Allow small floating point difference
        
        return (
            <div className={`card mb-4 ${isBalanced ? 'border-success' : 'border-danger'}`}>
                <div className="card-body">
                    <h5 className="mb-3">Accounting Equation Verification</h5>
                    <div className="row">
                        <div className="col-md-4 text-center">
                            <div className="p-3 bg-light rounded">
                                <h6>Assets</h6>
                                <h4 className="text-primary">
                                    {balanceSheetActions.formatCurrency(totalLeft)}
                                </h4>
                            </div>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="p-3 bg-light rounded">
                                <h6>Should Equal</h6>
                                <h4 className="text-muted">=</h4>
                            </div>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="p-3 bg-light rounded">
                                <h6>Liabilities + Equity</h6>
                                <h4 className="text-success">
                                    {balanceSheetActions.formatCurrency(totalRight)}
                                </h4>
                            </div>
                        </div>
                    </div>
                    
                    <div className="text-center mt-3">
                        {isBalanced ? (
                            <div className="alert alert-success mb-0">
                                <CheckCircle size={18} className="me-2" />
                                <strong>Balanced!</strong> The accounting equation is satisfied.
                                {difference > 0 && (
                                    <small className="ms-2">(Difference: {balanceSheetActions.formatCurrency(difference)})</small>
                                )}
                            </div>
                        ) : (
                            <div className="alert alert-danger mb-0">
                                <XCircle size={18} className="me-2" />
                                <strong>Unbalanced!</strong> Assets ({balanceSheetActions.formatCurrency(totalLeft)}) do not equal 
                                Liabilities + Equity ({balanceSheetActions.formatCurrency(totalRight)})
                                <br />
                                <small>Difference: {balanceSheetActions.formatCurrency(difference)}</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };
    
    // View mode selector
    const renderViewModeSelector = () => {
        const viewOptions = [
            { value: 'summary', label: 'Summary View', icon: <FileText size={16} /> },
            { value: 'detailed', label: 'Detailed View', icon: <List size={16} /> },
            { value: 'comparative', label: 'Comparative', icon: <BarChart2 size={16} /> }
        ];
        
        return (
            <div className="btn-group btn-group-sm me-2">
                {viewOptions.map(option => (
                    <button
                        key={option.value}
                        className={`btn ${viewMode === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode(option.value)}
                        title={option.label}
                    >
                        {option.icon}
                        <span className="ms-1 d-none d-md-inline">{option.label}</span>
                    </button>
                ))}
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !balanceSheet) {
        return <span>Loading...</span>;
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Balance Sheet</h4>
                            <h6>View your financial position at a glance</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <div className="d-flex align-items-center">
                                <Calendar size={16} className="me-2 text-muted" />
                                <DatePicker
                                    selected={asOnDate}
                                    onChange={handleDateChange}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control form-control-sm"
                                    maxDate={new Date()}
                                />
                            </div>
                        </li>
                        <li>
                            <div className="form-check form-switch ms-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showZeroBalances"
                                    checked={showZeroBalances}
                                    onChange={handleToggleZeroBalances}
                                />
                                <label className="form-check-label" htmlFor="showZeroBalances">
                                    Show Zero Balances
                                </label>
                            </div>
                        </li>
                        <li>
                            {renderViewModeSelector()}
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <File size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button 
                                    className="btn btn-outline-secondary" 
                                    onClick={handleRefresh}
                                    disabled={loading}
                                >
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Header Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>As on:</strong> {asOnDate.toLocaleDateString('en-IN', {
                                            day: '2-digit',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </div>
                                    <div>
                                        <strong>Report Type:</strong> {
                                            viewMode === 'summary' ? 'Summary View' :
                                            viewMode === 'detailed' ? 'Detailed View' : 'Comparative View'
                                        }
                                    </div>
                                    <div>
                                        <strong>Zero Balances:</strong> {showZeroBalances ? 'Shown' : 'Hidden'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Main Content */}
                {viewMode === 'summary' ? (
                    // Summary View - Just totals by category
                    <div className="row">
                        <div className="col-md-4">
                            <div className="card mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0">Assets</h5>
                                </div>
                                <div className="card-body">
                                    {balanceSheet?.assets.categories.map((cat, idx) => (
                                        <div key={idx} className="d-flex justify-content-between mb-2">
                                            <span>{cat.category}</span>
                                            <span className="text-primary">
                                                {balanceSheetActions.formatCurrency(cat.total)}
                                            </span>
                                        </div>
                                    ))}
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <strong>Total Assets</strong>
                                        <strong className="text-primary">
                                            {balanceSheetActions.formatCurrency(balanceSheet?.assets.total)}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-4">
                            <div className="card mb-4">
                                <div className="card-header bg-danger text-white">
                                    <h5 className="mb-0">Liabilities</h5>
                                </div>
                                <div className="card-body">
                                    {balanceSheet?.liabilities.categories.map((cat, idx) => (
                                        <div key={idx} className="d-flex justify-content-between mb-2">
                                            <span>{cat.category}</span>
                                            <span className="text-danger">
                                                {balanceSheetActions.formatCurrency(cat.total)}
                                            </span>
                                        </div>
                                    ))}
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <strong>Total Liabilities</strong>
                                        <strong className="text-danger">
                                            {balanceSheetActions.formatCurrency(balanceSheet?.liabilities.total)}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-4">
                            <div className="card mb-4">
                                <div className="card-header bg-success text-white">
                                    <h5 className="mb-0">Equity</h5>
                                </div>
                                <div className="card-body">
                                    {balanceSheet?.equity.categories.map((cat, idx) => (
                                        <div key={idx} className="d-flex justify-content-between mb-2">
                                            <span>{cat.category}</span>
                                            <span className="text-success">
                                                {balanceSheetActions.formatCurrency(cat.total)}
                                            </span>
                                        </div>
                                    ))}
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <strong>Total Equity</strong>
                                        <strong className="text-success">
                                            {balanceSheetActions.formatCurrency(balanceSheet?.equity.total)}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-12">
                            {renderEquationVerification()}
                        </div>
                    </div>
                ) : (
                    // Detailed View
                    <div className="row">
                        <div className="col-md-12">
                            {renderAssets()}
                            {renderLiabilities()}
                            {renderEquity()}
                            {renderEquationVerification()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BalanceSheet;